import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Trash2, Plus, User, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

/**
 * /settings/emergency — Slice J · Emergency contact list.
 *
 * Up to 3 trusted contacts (family, roommates, colleagues) receive an SMS
 * with the public live-trip link every time the user hits Panic on a ride.
 * The list itself is private to the user — never surfaced to drivers.
 */
export default function EmergencyContacts() {
  const [contacts, setContacts] = useState([]);
  const [max, setMax]           = useState(3);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [form, setForm]         = useState({ name: "", mobile: "", relationship: "" });
  useSeo({ title: "Emergency Contacts — Find2Near" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/emergency/my-contacts");
      setContacts(r.data?.contacts || []);
      setMax(r.data?.max || 3);
    } catch (e) {
      toast.error("Please sign in to manage emergency contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canAdd = contacts.length < max;

  const add = async (e) => {
    e.preventDefault();
    const name   = form.name.trim();
    const mobile = form.mobile.trim();
    if (!name || !mobile) { toast.error("Name and mobile are required"); return; }
    setSaving(true);
    try {
      await api.post("/emergency/my-contacts", {
        name, mobile,
        relationship: form.relationship.trim() || null,
      });
      setForm({ name: "", mobile: "", relationship: "" });
      toast.success("Emergency contact added");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save contact";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (contact_id) => {
    if (!window.confirm("Remove this emergency contact?")) return;
    try {
      await api.delete(`/emergency/my-contacts/${contact_id}`);
      toast.success("Contact removed");
      load();
    } catch {
      toast.error("Couldn't remove contact");
    }
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white pb-20" data-testid="emergency-contacts">
      <div className="flex items-center gap-3 px-4 pt-5 pb-4 border-b border-stone-100">
        <Link to="/account" className="p-2 -ml-2 rounded-full hover:bg-stone-100" data-testid="emerg-back">
          <ArrowLeft size={20} />
        </Link>
        <h1 className="heading font-black text-2xl text-stone-900">Emergency Contacts</h1>
      </div>

      <div className="p-4">
        <div className="rounded-2xl bg-rose-50 border border-rose-100 p-4 flex gap-3">
          <ShieldAlert size={22} className="text-rose-600 shrink-0 mt-0.5" />
          <div className="text-sm text-rose-900">
            When you press <b>Panic</b> during a live trip, we&apos;ll SMS every
            contact below the public tracking link so they can watch you until
            you arrive. Add up to <b>{max}</b> people you trust.
          </div>
        </div>

        {/* List */}
        <div className="mt-5">
          <div className="text-xs font-bold uppercase tracking-wide text-stone-500 mb-2">
            Your contacts {loading ? "" : `(${contacts.length}/${max})`}
          </div>
          {loading && <div className="text-sm text-stone-400 py-6 text-center">Loading…</div>}
          {!loading && contacts.length === 0 && (
            <div className="text-sm text-stone-500 bg-stone-50 rounded-xl p-4 text-center" data-testid="emerg-empty">
              No emergency contacts yet. Add one below.
            </div>
          )}
          {!loading && contacts.map(c => (
            <div
              key={c.contact_id}
              className="flex items-center gap-3 py-3 border-b border-stone-100 last:border-0"
              data-testid={`emerg-row-${c.contact_id}`}
            >
              <div className="w-10 h-10 rounded-full bg-emerald-100 grid place-items-center shrink-0">
                <User size={18} className="text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate">{c.name}</div>
                <div className="text-xs text-stone-500 truncate">
                  <Phone size={11} className="inline -mt-0.5 mr-0.5" />
                  {c.mobile}{c.relationship ? ` · ${c.relationship}` : ""}
                </div>
              </div>
              <button
                onClick={() => remove(c.contact_id)}
                className="p-2 rounded-full text-rose-600 hover:bg-rose-50"
                aria-label="Remove"
                data-testid={`emerg-remove-${c.contact_id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Add form */}
        {canAdd && !loading && (
          <form onSubmit={add} className="mt-5 rounded-2xl border border-stone-200 p-4 space-y-3" data-testid="emerg-form">
            <div className="text-xs font-bold uppercase tracking-wide text-stone-500">Add a contact</div>
            <input
              type="text"
              placeholder="Name (e.g. Mom)"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm"
              maxLength={80}
              data-testid="emerg-input-name"
            />
            <input
              type="tel"
              placeholder="Mobile (+91 98…)"
              value={form.mobile}
              onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
              className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm"
              maxLength={20}
              data-testid="emerg-input-mobile"
            />
            <input
              type="text"
              placeholder="Relationship (optional)"
              value={form.relationship}
              onChange={e => setForm(f => ({ ...f, relationship: e.target.value }))}
              className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm"
              maxLength={40}
              data-testid="emerg-input-rel"
            />
            <button
              type="submit"
              disabled={saving}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white font-bold rounded-full py-2.5 inline-flex items-center justify-center gap-2 text-sm"
              data-testid="emerg-add-btn"
            >
              <Plus size={16} /> {saving ? "Saving…" : "Add contact"}
            </button>
          </form>
        )}
        {!canAdd && !loading && (
          <div className="mt-4 text-xs text-stone-500 text-center">
            You&apos;ve reached the maximum of {max} contacts. Remove one to add another.
          </div>
        )}
      </div>
    </div>
  );
}
